import { useEventListener } from '@vueuse/core'

/**
 * Detects exit intent as a candidate validated over a short window before anything is sent
 * downstream. Desktop: mouse exits viewport. Mobile: fast upward scroll near top of page.
 */
export type ExitIntentReason = 'mouse_exit_viewport' | 'fast_scroll_up' | 'debug_trigger'

// Lifecycle transitions a consumer may want to observe for debugging/QA — the final payload
// delivery is still via the main callback, this is candidate-raised / candidate-aborted only.
export type ExitIntentLifecycleEvent = 'candidate' | 'abort'

type ExitIntentProduct = {
  product_id: string
  variant_id: string
  product_name: string | undefined
  quantity: number
  price: number | undefined
  image_url: string | undefined
  metadata: {
    is_clearance: boolean
    primary_category: string | null
  }
}

export type ExitIntentPayload = {
  // Detection context
  reason: ExitIntentReason
  exit_from_url: string
  exit_from_path: string
  // Cart context — captured at the moment of detection
  cart_id: string | null
  cart_total: number | null
  cart_currency: string | null
  cart_item_count: number
  cart_has_clearance_items: boolean
  // Top-level (not per-product) so the Braze campaign editor can match on it — nested array values aren't filterable.
  collections: string
  products: ExitIntentProduct[]
}

export function buildExitIntentPayload(reason: ExitIntentReason): ExitIntentPayload {
  const basket = useBasketsStore().basket
  const products: ExitIntentProduct[] =
    basket?.productItems?.map((item) => ({
      product_id: item.masterId ?? item.productId,
      variant_id: item.productId,
      product_name: item.productName,
      quantity: item.quantity ?? 1,
      price: item.price,
      image_url: item.image?.url,
      metadata: {
        is_clearance: item.productCustomAttributes?.c_isClearance ?? false,
        primary_category: item.productCustomAttributes?.c_mHJPrimaryCategory ?? null,
      },
    })) ?? []

  const collections = [
    ...new Set(
      basket?.productItems
        ?.map((item) => item.productCustomAttributes?.c_mHJCollection)
        .filter((collection): collection is string => Boolean(collection)) ?? [],
    ),
  ].join(', ')

  return {
    reason,
    exit_from_url: `${window.location.origin}${window.location.pathname}`,
    exit_from_path: window.location.pathname,
    cart_id: basket?.basketId ?? null,
    cart_total: basket?.orderTotal ?? null,
    cart_currency: basket?.currency ?? null,
    cart_item_count: basket?.productItems?.length ?? 0,
    cart_has_clearance_items:
      basket?.productItems?.some((item) => item.productCustomAttributes?.c_isClearance) ?? false,
    collections,
    products,
  }
}

// Reload/quit-adjacent editing shortcuts (r, F5, a/c/v/x/z/y/f...) are deliberately excluded —
// those keep the customer on the page, or are ordinary page input, and must still abort.
const BROWSER_EXIT_SHORTCUT_KEYS = new Set(['w', 'q', 't', 'n'])

// Note: browser-reserved shortcuts often aren't delivered to the page at all in some browsers,
// so this allowlist is defensive rather than load-bearing in every environment.
// isApplePlatform is passed in (rather than read from navigator here) so it's a single,
// init-time read — see isApplePlatform in useExitIntent below.
function isBrowserExitShortcut(event: KeyboardEvent, isApplePlatform: boolean): boolean {
  // Windows reports AltGr as Ctrl+Alt (altKey); other platforms only expose it via
  // getModifierState('AltGraph') — both are checked so AltGr typing (e.g. €, @, ~) isn't mistaken for a Ctrl shortcut.
  if (event.altKey || event.getModifierState?.('AltGraph')) return false
  // macOS accelerators use Cmd, not Ctrl: Ctrl+N/Ctrl+T there are Cocoa text-editing bindings,
  // so Ctrl must never count as an accelerator on Apple platforms.
  if (!(isApplePlatform ? event.metaKey : event.ctrlKey)) return false
  // Matched on `key`, not `code`: on AZERTY the `Z`-labelled key sits at physical KeyW, so
  // matching `code` would wrongly exempt Ctrl+Z (undo) as if it were Ctrl+W.
  return BROWSER_EXIT_SHORTCUT_KEYS.has(event.key.toLowerCase())
}

export function useExitIntent(
  onExitIntent: (payload: ExitIntentPayload, isImmediateExit: boolean) => void,
  onLifecycleEvent?: (event: ExitIntentLifecycleEvent, reason: ExitIntentReason) => void,
): void {
  // ── Configurable thresholds ──────────────────────────────────────────────
  // Mobile: how close to the page top the scroll signal must fire, viewport-relative (22%) so
  // it scales across device sizes rather than using a fixed px value.
  const TOP_OFFSET_PX = Math.round(window.innerHeight * 0.22)

  // Mobile: minimum upward scroll distance (px) within the time window.
  // Filters out tiny incidental scroll movements near the top of the page.
  const SCROLL_THRESHOLD_PX = 30

  // Mobile: maximum time window (ms) in which SCROLL_THRESHOLD_PX must be covered.
  // Combined with the distance threshold this confirms deliberate velocity / intent.
  const SCROLL_TIME_MS = 300

  // How long a candidate waits, uncontradicted, before it commits. Mobile is longer because
  // scroll-based re-engagement signals are noisier than desktop ones.
  const DESKTOP_VALIDATION_WINDOW_MS = 2000
  const MOBILE_VALIDATION_WINDOW_MS = 2500

  // Cumulative downward scroll (px) while a candidate is pending counts as re-engagement.
  // Accumulated rather than checked per-event so smooth/momentum scrolling — many small deltas
  // — still crosses the threshold instead of never tripping a single-event check.
  const DOWNWARD_SCROLL_ABORT_PX = 20

  // Same-gesture grace for fast_scroll_up candidates: iOS reports scrollY recoiling toward 0
  // after elastic overscroll, which reads as a downward delta but isn't re-engagement.
  const SCROLL_ABORT_GRACE_MS = 500

  // Cooldown (ms) after a commit OR an abort before a new candidate can be raised.
  // Prevents both duplicate fires from one gesture and abort/re-arm thrash (e.g. rapid
  // mouseleave/re-enter). Frequency across sessions is controlled by the consumer.
  const REARM_COOLDOWN_MS = 2000
  // ────────────────────────────────────────────────────────────────────────

  let candidate: ExitIntentPayload | null = null
  let candidateArmedAt = 0
  let candidateTimer: ReturnType<typeof setTimeout> | null = null
  let cooldownUntil = 0
  let downwardScrollAccumPx = 0

  function clearTimer() {
    if (candidateTimer !== null) {
      clearTimeout(candidateTimer)
      candidateTimer = null
    }
  }

  // Built at candidate creation, not commit time — capture it before a pagehide/visibilitychange
  // can end the page.
  function raiseCandidate(reason: ExitIntentReason, windowMs: number) {
    if (candidate || Date.now() < cooldownUntil) return
    candidate = buildExitIntentPayload(reason)
    candidateArmedAt = Date.now()
    downwardScrollAccumPx = 0
    candidateTimer = setTimeout(() => commit(false), windowMs)
    onLifecycleEvent?.('candidate', reason)
  }

  function abort() {
    if (!candidate) return
    onLifecycleEvent?.('abort', candidate.reason)
    clearTimer()
    candidate = null
    cooldownUntil = Date.now() + REARM_COOLDOWN_MS
  }

  // isImmediateExit distinguishes a genuine page exit (visibilitychange/pagehide) from a normal
  // window-expiry commit, so the caller knows whether to force-flush.
  function commit(isImmediateExit: boolean) {
    if (!candidate) return
    const payload = candidate
    clearTimer()
    candidate = null
    cooldownUntil = Date.now() + REARM_COOLDOWN_MS
    onExitIntent(payload, isImmediateExit)
  }

  // Desktop: cursor exits any of the four viewport edges (matches Barilliance's approach).
  // Top edge is the most common path (toward address bar), but all four are valid signals.
  function onDesktopExitIntent(e: MouseEvent) {
    if (
      e.clientY <= 0 || // top — toward address bar / browser chrome
      e.clientX <= 0 || // left
      e.clientX >= window.innerWidth || // right
      e.clientY >= window.innerHeight // bottom
    ) {
      raiseCandidate('mouse_exit_viewport', DESKTOP_VALIDATION_WINDOW_MS)
    }
  }

  // Mobile: fast upward scroll while near the top of the page raises a candidate. The same
  // event also carries the downward-scroll abort signal for any pending candidate.
  let lastScrollY = window.scrollY
  let lastScrollTime = Date.now()

  function onScroll() {
    const currentY = window.scrollY
    const now = Date.now()
    const deltaY = lastScrollY - currentY // positive = scrolled upward, negative = downward
    const deltaTime = now - lastScrollTime

    if (
      !candidate &&
      isTouchDevice &&
      currentY <= TOP_OFFSET_PX &&
      deltaY >= SCROLL_THRESHOLD_PX &&
      deltaTime <= SCROLL_TIME_MS
    ) {
      raiseCandidate('fast_scroll_up', MOBILE_VALIDATION_WINDOW_MS)
    } else if (candidate) {
      // scrollY <= 0 is the elastic overscroll region (iOS rubber-band), not user-driven
      // scrolling, and must not cancel the fast_scroll_up candidate that gesture just raised.
      const inOverscrollRegion = currentY <= 0
      const inScrollGrace = candidate.reason === 'fast_scroll_up' && now - candidateArmedAt < SCROLL_ABORT_GRACE_MS
      if (!inOverscrollRegion && !inScrollGrace) {
        if (deltaY < 0) {
          downwardScrollAccumPx += -deltaY
          if (downwardScrollAccumPx >= DOWNWARD_SCROLL_ABORT_PX) abort()
        } else if (deltaY > 0) {
          // Direction reversed — the prior downward movement wasn't sustained, so don't let it
          // combine with a later, unrelated downward scroll.
          downwardScrollAccumPx = 0
        }
      }
    }

    lastScrollY = currentY
    lastScrollTime = now
  }

  function onKeydownAbort(event: KeyboardEvent) {
    // Only the specific shortcuts that end/background the page skip abort, so the subsequent
    // pagehide/visibilitychange commit still has a candidate; everything else — including
    // modifier-assisted editing (Ctrl/Cmd+A/C/V/Z...) — is page engagement and must abort.
    if (isBrowserExitShortcut(event, isApplePlatform)) return
    abort()
  }

  // pointer: coarse = touchscreen (mobile/tablet), pointer: fine = mouse (desktop/laptop).
  // Checked once at initialisation — no need to re-evaluate during the session.
  const isTouchDevice = window.matchMedia('(pointer: coarse)').matches

  // navigator.platform is deprecated but kept as a fallback — userAgentData isn't available in
  // every browser yet. Checked once at initialisation, same as isTouchDevice above.
  const isApplePlatform = /mac|iphone|ipad|ipod/i.test(
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? navigator.platform,
  )

  if (!isTouchDevice) {
    useEventListener(document, 'mouseleave', onDesktopExitIntent)
  }
  useEventListener(window, 'scroll', onScroll, { passive: true })

  // Add-to-cart and checkout-start are covered implicitly: both require a preceding
  // click/tap/keypress, which already aborts, so no separate Pinia store subscription is added.
  useEventListener(document, 'mousemove', abort)
  useEventListener(document, 'click', abort)
  useEventListener(document, 'keydown', onKeydownAbort)
  useEventListener(document, 'touchstart', abort)

  // Nuxt awaits global middleware inside its own beforeEach, so any router guard we register
  // runs too late — page:loading:start fires first, before middleware and before cancellation.
  const stopRouteWatch = useNuxtApp().hook('page:loading:start', () => {
    abort()
  })
  onScopeDispose(() => {
    stopRouteWatch()
    clearTimer()
  })

  // A genuine exit commits immediately, bypassing the validation window. beforeunload is
  // deliberately not used — it blocks bfcache and is unreliable. The caller flushes downstream.
  useEventListener(document, 'visibilitychange', () => {
    if (document.visibilityState === 'hidden') commit(true)
  })
  useEventListener(window, 'pagehide', () => commit(true))
}
